import type { StaticEntityType } from "@config";
import { nanoid } from "@reduxjs/toolkit";
import { produce } from "immer";

type NestedEntity = { id: string; children?: NestedEntity[] };

/**
 * Fresh ids for EVERY level of a nested slide structure.
 *
 * The layout-apply / AI-regenerate / saved-slide "replace" flow feeds its
 * payload to `changeEntityLayout`, which normalizes with `keepIds: true` and
 * merges the entities verbatim — so this is the only thing standing between two
 * slides built from the same source and a shared subtree.
 *
 * Shared ids break hard: `isEntityActive` compares Redux ids, so both copies
 * render as active, both mount a Froala instance, and they collide on the same
 * DOM id — leaving one of them permanently uninitialised (no contenteditable,
 * no toolbar `refresh`, no char counter, and no error anywhere).
 *
 * The draft is mutated in place. `produce` is pure, so recursing into it and
 * dropping the return value silently left everything below the direct children
 * on its original ids. `children` is absent on leaves (see `invertElement` in
 * `@/shared/utils/normalizer`), hence the optional chaining.
 */
export const resetSlideIds = (entity: StaticEntityType): StaticEntityType =>
  produce(entity, (draft) => {
    const walk = (node: NestedEntity) => {
      node.id = nanoid();
      node.children?.forEach(walk);
    };
    walk(draft as NestedEntity);
  });
